import {
  Box,
  Checkbox,
  Flex,
  Heading,
  Input,
  Spinner,
  Stack,
  Table,
  Text,
  Textarea,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  FiArrowLeft,
  FiEdit2,
  FiFileText,
  FiPlus,
  FiTrash2,
} from "react-icons/fi"

import { OpenAPI } from "@/client"
import { IconBox, VisionButton, VisionCard } from "@/components/VisionUI"
import {
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "@/components/ui/dialog"
import { useOrganization } from "@/contexts/OrganizationContext"
import useCustomToast from "@/hooks/useCustomToast"
import { useSemanticColors } from "@/hooks/useSemanticColors"

export const Route = createFileRoute("/_layout/admin-dpp/records")({
  component: AdminDppRecords,
})

interface DppRecordRow {
  id: string
  organisation_id: string
  gs1_sku: string
  product_description?: string | null
  product_manufacturer_id?: string | null
  product_representative_id?: string | null
  co2e_kg?: number | null
  co2e_methodology?: string | null
  repairability_score?: number | null
  repairability_notes?: string | null
  enabled?: boolean
}

interface NamedRow {
  id: string
  name: string
}

function authedFetch(path: string, init: RequestInit = {}) {
  const token = localStorage.getItem("access_token")
  const base = (OpenAPI.BASE || "").replace(/\/$/, "")
  return fetch(`${base}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      ...(init.headers || {}),
    },
  })
}

function AdminDppRecords() {
  const sc = useSemanticColors()
  const { t } = useTranslation("dpp")
  const navigate = useNavigate()
  const { currentOrganization } = useOrganization()
  const queryClient = useQueryClient()
  const { showToast } = useCustomToast()
  const orgId = currentOrganization?.id

  // ---- pagination + search ----
  const [searchInput, setSearchInput] = useState("")
  const [search, setSearch] = useState("")
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(50)
  useEffect(() => {
    const id = window.setTimeout(() => {
      setSearch(searchInput.trim())
      setPage(0)
    }, 350)
    return () => window.clearTimeout(id)
  }, [searchInput])

  const listQuery = useQuery({
    queryKey: ["dpp-records", orgId, page, pageSize, search],
    queryFn: async (): Promise<{ data: DppRecordRow[]; count: number }> => {
      const params = new URLSearchParams({
        skip: String(page * pageSize),
        limit: String(pageSize),
      })
      // The CRUD list endpoint (see backend/app/api/routes/dpp_record.py)
      // returns a {data, count} envelope. SKU search is applied client
      // side until the backend grows a `search` param — fine for small
      // page sizes.
      const r = await authedFetch(
        `/api/v1/organizations/${orgId}/dpp-records/?${params.toString()}`,
      )
      if (!r.ok) throw new Error(await r.text())
      return r.json()
    },
    enabled: !!orgId,
    staleTime: 30_000,
  })

  // Client-side filter on the current page by SKU substring. The backend
  // list endpoint doesn't yet expose a search param, so this is a
  // best-effort match against the rows we already fetched. Operators
  // with thousands of records can still find a known SKU by paging.
  const filteredRows = useMemo(() => {
    const all = listQuery.data?.data ?? []
    if (!search) return all
    const q = search.toLowerCase()
    return all.filter((r) => r.gs1_sku.toLowerCase().includes(q))
  }, [listQuery.data?.data, search])

  const totalCount = listQuery.data?.count ?? 0
  const pageCount = Math.max(1, Math.ceil(totalCount / pageSize))
  const rangeStart = totalCount === 0 ? 0 : page * pageSize + 1
  const rangeEnd = Math.min(
    totalCount,
    page * pageSize + (listQuery.data?.data?.length ?? 0),
  )

  // Manufacturer + representative pickers for the dialog.
  const manufacturersQuery = useQuery({
    queryKey: ["manufacturers", orgId, "all"],
    queryFn: async (): Promise<NamedRow[]> => {
      const r = await authedFetch(
        `/api/v1/organizations/${orgId}/manufacturers/?limit=500`,
      )
      if (!r.ok) return []
      const j = (await r.json()) as { data?: NamedRow[] }
      return j.data ?? []
    },
    enabled: !!orgId,
    staleTime: 60_000,
  })
  const representativesQuery = useQuery({
    queryKey: ["representatives", orgId, "all"],
    queryFn: async (): Promise<NamedRow[]> => {
      const r = await authedFetch(
        `/api/v1/organizations/${orgId}/representatives/?limit=500`,
      )
      if (!r.ok) return []
      const j = (await r.json()) as { data?: NamedRow[] }
      return j.data ?? []
    },
    enabled: !!orgId,
    staleTime: 60_000,
  })
  const manufacturers = manufacturersQuery.data ?? []
  const representatives = representativesQuery.data ?? []
  const manufacturerById = useMemo(
    () => new Map(manufacturers.map((m) => [m.id, m])),
    [manufacturers],
  )
  const representativeById = useMemo(
    () => new Map(representatives.map((r) => [r.id, r])),
    [representatives],
  )

  // ---- dialog ----
  const [isOpen, setIsOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [draft, setDraft] = useState({
    gs1_sku: "",
    product_description: "",
    product_manufacturer_id: "",
    product_representative_id: "",
    co2e_kg: "",
    co2e_methodology: "",
    repairability_score: "",
    repairability_notes: "",
  })

  const resetDraft = () => {
    setEditingId(null)
    setDraft({
      gs1_sku: "",
      product_description: "",
      product_manufacturer_id: "",
      product_representative_id: "",
      co2e_kg: "",
      co2e_methodology: "",
      repairability_score: "",
      repairability_notes: "",
    })
  }
  const seedFromRow = (row: DppRecordRow) => {
    setEditingId(row.id)
    setDraft({
      gs1_sku: row.gs1_sku ?? "",
      product_description: row.product_description ?? "",
      product_manufacturer_id: row.product_manufacturer_id ?? "",
      product_representative_id: row.product_representative_id ?? "",
      co2e_kg: row.co2e_kg != null ? String(row.co2e_kg) : "",
      co2e_methodology: row.co2e_methodology ?? "",
      repairability_score:
        row.repairability_score != null ? String(row.repairability_score) : "",
      repairability_notes: row.repairability_notes ?? "",
    })
    setIsOpen(true)
  }

  // ---- inline "+ Create" sub-dialogs for manufacturer / representative ----
  // Open via the sentinel ``__create__`` value in the parent dropdowns.
  // After a successful save we refetch the catalog and auto-select the
  // freshly-created row on the DPP record draft.
  const emptyContact = {
    name: "",
    address: "",
    email: "",
    phone: "",
    website: "",
  }
  const [mfrCreateOpen, setMfrCreateOpen] = useState(false)
  const [mfrDraft, setMfrDraft] = useState({ ...emptyContact })
  const [repCreateOpen, setRepCreateOpen] = useState(false)
  const [repDraft, setRepDraft] = useState({ ...emptyContact })

  const createMfrMutation = useMutation({
    mutationFn: async (): Promise<{ id: string }> => {
      if (!orgId) throw new Error("No organisation selected")
      const body = {
        organisation_id: orgId,
        name: mfrDraft.name.trim(),
        address: mfrDraft.address.trim() || null,
        email: mfrDraft.email.trim() || null,
        phone: mfrDraft.phone.trim() || null,
        website: mfrDraft.website.trim() || null,
      }
      const res = await authedFetch(
        `/api/v1/organizations/${orgId}/manufacturers/`,
        { method: "POST", body: JSON.stringify(body) },
      )
      if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`)
      return res.json()
    },
    onSuccess: async (created) => {
      // Refetch the catalogue so the new row shows up, then auto-select
      // it on the DPP record draft.
      await queryClient.invalidateQueries({
        queryKey: ["manufacturers", orgId, "all"],
      })
      setDraft((d) => ({ ...d, product_manufacturer_id: created.id }))
      showToast(
        t("records.toast.mfrCreatedTitle", "Manufacturer created"),
        t("records.toast.mfrCreatedBody", "Linked to this DPP record."),
        "success",
      )
      setMfrCreateOpen(false)
      setMfrDraft({ ...emptyContact })
    },
    onError: (err: unknown) => {
      showToast(
        t("records.toast.mfrCreateErrorTitle", "Could not create manufacturer"),
        err instanceof Error ? err.message : String(err),
        "error",
      )
    },
  })

  const createRepMutation = useMutation({
    mutationFn: async (): Promise<{ id: string }> => {
      if (!orgId) throw new Error("No organisation selected")
      const body = {
        organisation_id: orgId,
        name: repDraft.name.trim(),
        address: repDraft.address.trim() || null,
        email: repDraft.email.trim() || null,
        phone: repDraft.phone.trim() || null,
        website: repDraft.website.trim() || null,
      }
      const res = await authedFetch(
        `/api/v1/organizations/${orgId}/representatives/`,
        { method: "POST", body: JSON.stringify(body) },
      )
      if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`)
      return res.json()
    },
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({
        queryKey: ["representatives", orgId, "all"],
      })
      setDraft((d) => ({ ...d, product_representative_id: created.id }))
      showToast(
        t("records.toast.repCreatedTitle", "Representative created"),
        t("records.toast.repCreatedBody", "Linked to this DPP record."),
        "success",
      )
      setRepCreateOpen(false)
      setRepDraft({ ...emptyContact })
    },
    onError: (err: unknown) => {
      showToast(
        t("records.toast.repCreateErrorTitle", "Could not create representative"),
        err instanceof Error ? err.message : String(err),
        "error",
      )
    },
  })

  // ---- bulk select ----
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const allSelected =
    filteredRows.length > 0 && selectedIds.size === filteredRows.length
  const toggleSelection = (id: string, next: boolean) =>
    setSelectedIds((prev) => {
      const out = new Set(prev)
      if (next) out.add(id)
      else out.delete(id)
      return out
    })
  const toggleAll = (next: boolean) => {
    if (!next) setSelectedIds(new Set())
    else setSelectedIds(new Set(filteredRows.map((r) => r.id)))
  }

  // The by-sku upsert endpoint is the canonical write path so it
  // happily handles both create and update without an id round-trip.
  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!orgId) throw new Error("No organisation selected")
      const gs1_sku = draft.gs1_sku.trim()
      if (!gs1_sku) throw new Error("GS1 SKU is required")
      const body: Record<string, unknown> = {
        product_description: draft.product_description.trim() || null,
        product_manufacturer_id: draft.product_manufacturer_id || null,
        product_representative_id: draft.product_representative_id || null,
        co2e_kg:
          draft.co2e_kg.trim() === "" ? null : Number(draft.co2e_kg),
        co2e_methodology: draft.co2e_methodology.trim() || null,
        repairability_score:
          draft.repairability_score.trim() === ""
            ? null
            : Number(draft.repairability_score),
        repairability_notes: draft.repairability_notes.trim() || null,
      }
      const res = await authedFetch(
        `/api/v1/organizations/${orgId}/dpp-records/by-sku/${encodeURIComponent(gs1_sku)}`,
        { method: "PUT", body: JSON.stringify(body) },
      )
      if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`)
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dpp-records", orgId] })
      showToast(
        editingId
          ? t("records.toast.updatedTitle", "Record updated")
          : t("records.toast.createdTitle", "Record created"),
        t("records.toast.savedBody", "Saved."),
        "success",
      )
      setIsOpen(false)
      resetDraft()
    },
    onError: (err: unknown) => {
      showToast(
        t("records.toast.errorTitle", "Could not save record"),
        err instanceof Error ? err.message : String(err),
        "error",
      )
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      if (!orgId) throw new Error("No organisation selected")
      const results = await Promise.allSettled(
        ids.map((id) =>
          authedFetch(
            `/api/v1/organizations/${orgId}/dpp-records/${id}`,
            { method: "DELETE" },
          ).then(async (res) => {
            if (!res.ok) throw new Error(await res.text())
          }),
        ),
      )
      const failed = results.filter((r) => r.status === "rejected")
      if (failed.length > 0) {
        const detail = (failed[0] as PromiseRejectedResult).reason
        throw new Error(
          detail instanceof Error ? detail.message : String(detail),
        )
      }
    },
    onSuccess: (_unused, ids) => {
      queryClient.invalidateQueries({ queryKey: ["dpp-records", orgId] })
      setSelectedIds(new Set())
      if (editingId && ids.includes(editingId)) {
        setIsOpen(false)
        resetDraft()
      }
      showToast(
        t("records.toast.deletedTitle", "Record deleted"),
        t("records.toast.deletedBody", "{{count}} removed.", {
          count: ids.length,
        }),
        "success",
      )
    },
    onError: (err: unknown) => {
      showToast(
        t("records.toast.deleteErrorTitle", "Could not delete"),
        err instanceof Error ? err.message : String(err),
        "error",
      )
    },
  })

  const canSubmit = draft.gs1_sku.trim().length > 0

  return (
    <Box>
      <Flex mb={4} align="center" gap={3}>
        <VisionButton
          variant="ghost"
          size="sm"
          onClick={() => navigate({ to: "/admin-dpp" })}
        >
          <FiArrowLeft />
          <Text ml={2}>{t("common.backToOverview")}</Text>
        </VisionButton>
      </Flex>

      <Flex align="center" gap={3} mb={6} flexWrap="wrap">
        <IconBox variant="glass" size="lg">
          <FiFileText fontSize="24px" color={sc.iconColor} />
        </IconBox>
        <Box flex="1">
          <Heading size="lg" color={sc.heading}>
            {t("records.title", "DPP Records")}
          </Heading>
          <Text color={sc.textMuted}>
            {t(
              "records.subtitle",
              "Per-product DPP data — manufacturer, authorised representative, sustainability. Keyed on GS1 SKU; joined to tags by Tag.sku at render time.",
            )}
          </Text>
        </Box>
      </Flex>

      <VisionCard>
        <Flex
          justify="space-between"
          align="center"
          p={4}
          gap={3}
          flexWrap="wrap"
        >
          <Heading size="md" color={sc.heading}>
            {totalCount === 0
              ? t("records.empty", "No records")
              : t(
                  "records.listTitleRange",
                  "Showing {{start}}–{{end}} of {{total}}",
                  { start: rangeStart, end: rangeEnd, total: totalCount },
                )}
          </Heading>
          <Flex gap={2} align="center" flexWrap="wrap">
            <Input
              size="sm"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t("records.searchPlaceholder", "Search by SKU")}
              maxW="240px"
            />
            {selectedIds.size > 0 && (
              <VisionButton
                variant="ghost"
                size="sm"
                disabled={deleteMutation.isPending}
                onClick={() =>
                  deleteMutation.mutate(Array.from(selectedIds))
                }
              >
                <FiTrash2 />
                <Text ml={1}>
                  {t("records.deleteSelected", "Delete {{count}}", {
                    count: selectedIds.size,
                  })}
                </Text>
              </VisionButton>
            )}
            <VisionButton
              variant="brand"
              size="sm"
              onClick={() => {
                resetDraft()
                setIsOpen(true)
              }}
            >
              <FiPlus />
              <Text ml={1}>{t("records.add", "Add record")}</Text>
            </VisionButton>
          </Flex>
        </Flex>

        {listQuery.isLoading ? (
          <Flex justify="center" py={6}>
            <Spinner size="md" color={sc.spinnerColor} />
          </Flex>
        ) : filteredRows.length === 0 ? (
          <Text color={sc.textMuted} fontSize="sm" px={4} pb={4}>
            {search
              ? t("records.emptyForSearch", "No records match the search.")
              : t("records.empty", "No records yet.")}
          </Text>
        ) : (
          <Box overflowX="auto">
            <Table.Root size="sm">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader w="40px">
                    <Checkbox.Root
                      checked={allSelected}
                      onCheckedChange={(d) => toggleAll(!!d.checked)}
                      size="sm"
                      aria-label="Select all"
                    >
                      <Checkbox.HiddenInput />
                      <Checkbox.Control />
                    </Checkbox.Root>
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color={sc.textMuted}>
                    {t("records.col.sku", "GS1 SKU")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color={sc.textMuted}>
                    {t("records.col.manufacturer", "Manufacturer")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color={sc.textMuted}>
                    {t("records.col.representative", "Representative")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color={sc.textMuted}>
                    {t("records.col.co2e", "CO₂e (kg)")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color={sc.textMuted}>
                    {t("records.col.repairability", "Repairability")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader w="40px" />
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {filteredRows.map((row) => {
                  const isSelected = selectedIds.has(row.id)
                  const m = row.product_manufacturer_id
                    ? manufacturerById.get(row.product_manufacturer_id)
                    : undefined
                  const r = row.product_representative_id
                    ? representativeById.get(row.product_representative_id)
                    : undefined
                  return (
                    <Table.Row key={row.id}>
                      <Table.Cell>
                        <Checkbox.Root
                          checked={isSelected}
                          onCheckedChange={(d) =>
                            toggleSelection(row.id, !!d.checked)
                          }
                          size="sm"
                          aria-label={`Select ${row.gs1_sku}`}
                        >
                          <Checkbox.HiddenInput />
                          <Checkbox.Control />
                        </Checkbox.Root>
                      </Table.Cell>
                      <Table.Cell
                        color={sc.heading}
                        fontFamily="mono"
                        fontSize="sm"
                      >
                        {row.gs1_sku}
                      </Table.Cell>
                      <Table.Cell color={sc.textMuted} fontSize="sm">
                        {m?.name ?? "—"}
                      </Table.Cell>
                      <Table.Cell color={sc.textMuted} fontSize="sm">
                        {r?.name ?? "—"}
                      </Table.Cell>
                      <Table.Cell color={sc.textMuted} fontSize="sm">
                        {row.co2e_kg != null ? row.co2e_kg : "—"}
                      </Table.Cell>
                      <Table.Cell color={sc.textMuted} fontSize="sm">
                        {row.repairability_score != null
                          ? `${row.repairability_score} / 10`
                          : "—"}
                      </Table.Cell>
                      <Table.Cell>
                        <VisionButton
                          variant="ghost"
                          size="xs"
                          onClick={() => seedFromRow(row)}
                        >
                          <FiEdit2 />
                        </VisionButton>
                      </Table.Cell>
                    </Table.Row>
                  )
                })}
              </Table.Body>
            </Table.Root>
          </Box>
        )}

        {totalCount > 0 && (
          <Flex
            justify="space-between"
            align="center"
            p={4}
            gap={3}
            flexWrap="wrap"
            borderTop="1px solid"
            borderColor={sc.border}
          >
            <Flex gap={2} align="center">
              <Text fontSize="sm" color={sc.textMuted}>
                {t("records.pagination.pageSize", "Rows per page")}
              </Text>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value))
                  setPage(0)
                }}
                style={{
                  padding: "4px 6px",
                  borderRadius: 6,
                  border: "1px solid rgba(255,255,255,0.12)",
                  background: "rgba(255,255,255,0.06)",
                  color: "inherit",
                }}
              >
                {[25, 50, 100, 200].map((n) => (
                  <option key={n} value={n} style={{ background: "#1a202c" }}>
                    {n}
                  </option>
                ))}
              </select>
            </Flex>
            <Flex gap={2} align="center">
              <Text fontSize="sm" color={sc.textMuted}>
                {t("records.pagination.pageOf", "Page {{page}} of {{count}}", {
                  page: page + 1,
                  count: pageCount,
                })}
              </Text>
              <VisionButton
                variant="ghost"
                size="sm"
                disabled={page === 0}
                onClick={() => setPage(0)}
              >
                « First
              </VisionButton>
              <VisionButton
                variant="ghost"
                size="sm"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                ‹ Prev
              </VisionButton>
              <VisionButton
                variant="ghost"
                size="sm"
                disabled={page >= pageCount - 1}
                onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
              >
                Next ›
              </VisionButton>
              <VisionButton
                variant="ghost"
                size="sm"
                disabled={page >= pageCount - 1}
                onClick={() => setPage(pageCount - 1)}
              >
                Last »
              </VisionButton>
            </Flex>
          </Flex>
        )}
      </VisionCard>

      <DialogRoot
        open={isOpen}
        onOpenChange={(d) => {
          if (!d.open) resetDraft()
          setIsOpen(d.open)
        }}
        size="lg"
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingId
                ? t("records.dialog.editTitle", "Edit DPP record")
                : t("records.dialog.createTitle", "Add DPP record")}
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Stack gap={3}>
              <Box>
                <Text fontSize="sm" color={sc.textMuted} mb={1}>
                  {t("records.field.sku", "GS1 SKU")}
                </Text>
                <Input
                  value={draft.gs1_sku}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, gs1_sku: e.target.value }))
                  }
                  placeholder="00012345678905"
                  fontFamily="mono"
                  maxLength={255}
                  disabled={editingId !== null}
                />
                {editingId !== null && (
                  <Text fontSize="xs" color={sc.textMuted} mt={1}>
                    {t(
                      "records.field.skuLocked",
                      "GS1 SKU is the row key — to move data to a different SKU, delete and re-create.",
                    )}
                  </Text>
                )}
              </Box>
              <Box>
                <Text fontSize="sm" color={sc.textMuted} mb={1}>
                  {t("records.field.description", "Product description")}
                </Text>
                <Textarea
                  rows={2}
                  value={draft.product_description}
                  onChange={(e) =>
                    setDraft((d) => ({
                      ...d,
                      product_description: e.target.value,
                    }))
                  }
                  placeholder="What is this product?"
                  maxLength={2000}
                />
              </Box>
              <Flex gap={3} flexWrap="wrap">
                <Box flex="1" minW="220px">
                  <Text fontSize="sm" color={sc.textMuted} mb={1}>
                    {t("records.field.manufacturer", "Manufacturer")}
                  </Text>
                  <select
                    value={draft.product_manufacturer_id}
                    onChange={(e) => {
                      const v = e.target.value
                      if (v === "__create__") {
                        setMfrCreateOpen(true)
                        return
                      }
                      setDraft((d) => ({
                        ...d,
                        product_manufacturer_id: v,
                      }))
                    }}
                    style={{
                      width: "100%",
                      padding: "8px",
                      borderRadius: 8,
                      border: "1px solid rgba(255,255,255,0.12)",
                      background: "rgba(255,255,255,0.06)",
                      color: "inherit",
                    }}
                  >
                    <option value="" style={{ background: "#1a202c" }}>
                      {t("records.field.none", "— (none) —")}
                    </option>
                    <option
                      value="__create__"
                      style={{ background: "#1a202c" }}
                    >
                      {t(
                        "records.field.createNewManufacturer",
                        "+ Create new manufacturer…",
                      )}
                    </option>
                    {manufacturers.map((m) => (
                      <option
                        key={m.id}
                        value={m.id}
                        style={{ background: "#1a202c" }}
                      >
                        {m.name}
                      </option>
                    ))}
                  </select>
                </Box>
                <Box flex="1" minW="220px">
                  <Text fontSize="sm" color={sc.textMuted} mb={1}>
                    {t("records.field.representative", "Representative")}
                  </Text>
                  <select
                    value={draft.product_representative_id}
                    onChange={(e) => {
                      const v = e.target.value
                      if (v === "__create__") {
                        setRepCreateOpen(true)
                        return
                      }
                      setDraft((d) => ({
                        ...d,
                        product_representative_id: v,
                      }))
                    }}
                    style={{
                      width: "100%",
                      padding: "8px",
                      borderRadius: 8,
                      border: "1px solid rgba(255,255,255,0.12)",
                      background: "rgba(255,255,255,0.06)",
                      color: "inherit",
                    }}
                  >
                    <option value="" style={{ background: "#1a202c" }}>
                      {t("records.field.none", "— (none) —")}
                    </option>
                    <option
                      value="__create__"
                      style={{ background: "#1a202c" }}
                    >
                      {t(
                        "records.field.createNewRepresentative",
                        "+ Create new representative…",
                      )}
                    </option>
                    {representatives.map((r) => (
                      <option
                        key={r.id}
                        value={r.id}
                        style={{ background: "#1a202c" }}
                      >
                        {r.name}
                      </option>
                    ))}
                  </select>
                </Box>
              </Flex>
              <Flex gap={3} flexWrap="wrap">
                <Box flex="1" minW="160px">
                  <Text fontSize="sm" color={sc.textMuted} mb={1}>
                    {t("records.field.co2e", "CO₂e (kg)")}
                  </Text>
                  <Input
                    type="number"
                    step="any"
                    value={draft.co2e_kg}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, co2e_kg: e.target.value }))
                    }
                    placeholder="123.4"
                  />
                </Box>
                <Box flex="1" minW="160px">
                  <Text fontSize="sm" color={sc.textMuted} mb={1}>
                    {t("records.field.repairability", "Repairability (0–10)")}
                  </Text>
                  <Input
                    type="number"
                    min={0}
                    max={10}
                    value={draft.repairability_score}
                    onChange={(e) =>
                      setDraft((d) => ({
                        ...d,
                        repairability_score: e.target.value,
                      }))
                    }
                    placeholder="7"
                  />
                </Box>
              </Flex>
              <Box>
                <Text fontSize="sm" color={sc.textMuted} mb={1}>
                  {t("records.field.co2eMethodology", "CO₂e methodology")}
                </Text>
                <Input
                  value={draft.co2e_methodology}
                  onChange={(e) =>
                    setDraft((d) => ({
                      ...d,
                      co2e_methodology: e.target.value,
                    }))
                  }
                  placeholder="ISO 14067 cradle-to-gate"
                  maxLength={500}
                />
              </Box>
              <Box>
                <Text fontSize="sm" color={sc.textMuted} mb={1}>
                  {t(
                    "records.field.repairabilityNotes",
                    "Repairability notes",
                  )}
                </Text>
                <Textarea
                  rows={2}
                  value={draft.repairability_notes}
                  onChange={(e) =>
                    setDraft((d) => ({
                      ...d,
                      repairability_notes: e.target.value,
                    }))
                  }
                  placeholder="Spare parts available for 10 years"
                  maxLength={1000}
                />
              </Box>
            </Stack>
          </DialogBody>
          <DialogFooter>
            <Flex gap={2} w="100%" justify="space-between">
              <Box>
                {editingId && (
                  <VisionButton
                    variant="ghost"
                    size="sm"
                    colorPalette="red"
                    disabled={deleteMutation.isPending}
                    onClick={() => deleteMutation.mutate([editingId])}
                  >
                    <FiTrash2 />
                    <Text ml={1}>
                      {t("records.dialog.delete", "Delete")}
                    </Text>
                  </VisionButton>
                )}
              </Box>
              <Flex gap={2}>
                <VisionButton
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setIsOpen(false)
                    resetDraft()
                  }}
                >
                  {t("records.dialog.cancel", "Cancel")}
                </VisionButton>
                <VisionButton
                  variant="brand"
                  size="sm"
                  disabled={!canSubmit || saveMutation.isPending}
                  onClick={() => saveMutation.mutate()}
                >
                  {t("records.dialog.save", "Save")}
                </VisionButton>
              </Flex>
            </Flex>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>

      {/* Inline "create manufacturer" sub-dialog */}
      <DialogRoot
        open={mfrCreateOpen}
        onOpenChange={(d) => {
          if (!d.open) setMfrDraft({ ...emptyContact })
          setMfrCreateOpen(d.open)
        }}
        size="md"
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t(
                "records.mfrCreate.title",
                "New manufacturer",
              )}
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Stack gap={3}>
              <Box>
                <Text fontSize="sm" color={sc.textMuted} mb={1}>
                  {t("manufacturers.field.name", "Name")}
                </Text>
                <Input
                  value={mfrDraft.name}
                  onChange={(e) =>
                    setMfrDraft((d) => ({ ...d, name: e.target.value }))
                  }
                  placeholder="Acme Industries Inc."
                  maxLength={200}
                />
              </Box>
              <Box>
                <Text fontSize="sm" color={sc.textMuted} mb={1}>
                  {t("manufacturers.field.address", "Address")}
                </Text>
                <Input
                  value={mfrDraft.address}
                  onChange={(e) =>
                    setMfrDraft((d) => ({ ...d, address: e.target.value }))
                  }
                  placeholder="1234 Main St, Anytown, USA"
                  maxLength={500}
                />
              </Box>
              <Box>
                <Text fontSize="sm" color={sc.textMuted} mb={1}>
                  {t("manufacturers.field.email", "Email")}
                </Text>
                <Input
                  value={mfrDraft.email}
                  onChange={(e) =>
                    setMfrDraft((d) => ({ ...d, email: e.target.value }))
                  }
                  placeholder="contact@example.com"
                  maxLength={320}
                />
              </Box>
              <Box>
                <Text fontSize="sm" color={sc.textMuted} mb={1}>
                  {t("manufacturers.field.phone", "Phone")}
                </Text>
                <Input
                  value={mfrDraft.phone}
                  onChange={(e) =>
                    setMfrDraft((d) => ({ ...d, phone: e.target.value }))
                  }
                  placeholder="+1 555 123 4567"
                  maxLength={64}
                />
              </Box>
              <Box>
                <Text fontSize="sm" color={sc.textMuted} mb={1}>
                  {t("manufacturers.field.website", "Website")}
                </Text>
                <Input
                  value={mfrDraft.website}
                  onChange={(e) =>
                    setMfrDraft((d) => ({ ...d, website: e.target.value }))
                  }
                  placeholder="https://example.com"
                  maxLength={500}
                />
              </Box>
            </Stack>
          </DialogBody>
          <DialogFooter>
            <Flex gap={2} justify="flex-end" w="100%">
              <VisionButton
                variant="ghost"
                size="sm"
                onClick={() => {
                  setMfrCreateOpen(false)
                  setMfrDraft({ ...emptyContact })
                }}
              >
                {t("records.mfrCreate.cancel", "Cancel")}
              </VisionButton>
              <VisionButton
                variant="brand"
                size="sm"
                disabled={
                  mfrDraft.name.trim().length === 0 ||
                  createMfrMutation.isPending
                }
                onClick={() => createMfrMutation.mutate()}
              >
                {t("records.mfrCreate.save", "Create and link")}
              </VisionButton>
            </Flex>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>

      {/* Inline "create representative" sub-dialog */}
      <DialogRoot
        open={repCreateOpen}
        onOpenChange={(d) => {
          if (!d.open) setRepDraft({ ...emptyContact })
          setRepCreateOpen(d.open)
        }}
        size="md"
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t(
                "records.repCreate.title",
                "New representative",
              )}
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Stack gap={3}>
              <Box>
                <Text fontSize="sm" color={sc.textMuted} mb={1}>
                  {t("representatives.field.name", "Name")}
                </Text>
                <Input
                  value={repDraft.name}
                  onChange={(e) =>
                    setRepDraft((d) => ({ ...d, name: e.target.value }))
                  }
                  placeholder="EU Compliance Partners SARL"
                  maxLength={200}
                />
              </Box>
              <Box>
                <Text fontSize="sm" color={sc.textMuted} mb={1}>
                  {t("representatives.field.address", "Address")}
                </Text>
                <Input
                  value={repDraft.address}
                  onChange={(e) =>
                    setRepDraft((d) => ({ ...d, address: e.target.value }))
                  }
                  placeholder="Rue de la Loi 1, 1040 Brussels, Belgium"
                  maxLength={500}
                />
              </Box>
              <Box>
                <Text fontSize="sm" color={sc.textMuted} mb={1}>
                  {t("representatives.field.email", "Email")}
                </Text>
                <Input
                  value={repDraft.email}
                  onChange={(e) =>
                    setRepDraft((d) => ({ ...d, email: e.target.value }))
                  }
                  placeholder="contact@example.eu"
                  maxLength={320}
                />
              </Box>
              <Box>
                <Text fontSize="sm" color={sc.textMuted} mb={1}>
                  {t("representatives.field.phone", "Phone")}
                </Text>
                <Input
                  value={repDraft.phone}
                  onChange={(e) =>
                    setRepDraft((d) => ({ ...d, phone: e.target.value }))
                  }
                  placeholder="+32 2 555 1234"
                  maxLength={64}
                />
              </Box>
              <Box>
                <Text fontSize="sm" color={sc.textMuted} mb={1}>
                  {t("representatives.field.website", "Website")}
                </Text>
                <Input
                  value={repDraft.website}
                  onChange={(e) =>
                    setRepDraft((d) => ({ ...d, website: e.target.value }))
                  }
                  placeholder="https://example.eu"
                  maxLength={500}
                />
              </Box>
            </Stack>
          </DialogBody>
          <DialogFooter>
            <Flex gap={2} justify="flex-end" w="100%">
              <VisionButton
                variant="ghost"
                size="sm"
                onClick={() => {
                  setRepCreateOpen(false)
                  setRepDraft({ ...emptyContact })
                }}
              >
                {t("records.repCreate.cancel", "Cancel")}
              </VisionButton>
              <VisionButton
                variant="brand"
                size="sm"
                disabled={
                  repDraft.name.trim().length === 0 ||
                  createRepMutation.isPending
                }
                onClick={() => createRepMutation.mutate()}
              >
                {t("records.repCreate.save", "Create and link")}
              </VisionButton>
            </Flex>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>
    </Box>
  )
}
